<template>
<div class="flex-column checkout_product_row-border">
    <div class="products_row-item checkout_product_row-item">
        <div class="product_row-details" style="cursor:pointer;" target="_blank" @click="openBuyStackFlyout">
            <img :src="listItem.item.item_picture" :srcset="listItem.item.item_picture_srcset" :sizes="listItem.item.item_picture_sizes"
                :alt="listItem.item.item_name"
                class="products_row-card_image checkout_product_row-item_image" />
            <div class="product_row-description">
                <div class="title-xs">{{ listItem.item.item_name }}</div>
                <p class="product_row-id paragraph-details">#{{ listItem.item_no }}</p>
                <p class="product_row-size paragraph-details" ><span v-for="(variant, index) in (listItem?.item?.variants ?? [])" :key="index">
                    {{ variant }}&nbsp;</span>
                </p>
                <p v-if="props.freeItem && shouldShowBuystackButton" class="paragraph-details free-item-click-to-choose">
                    <b>Click to choose your {{ freeItemSpecificationGroupNames.join("/") }}</b>
                </p>
                <p v-if="showQuantityInDetails" class="product_row-quantity paragraph-details">Qty: {{ listItem.quantity }}</p>
            </div>
        </div>
        <div class="product_row-actions">
            <div class="products_row-quantity">
                <input type="number"
                    :ref="`list_item_${listItem.list_item_id}`"
                    class="products_row-quantity_field small"
                    :class="{ 'products_row-quantity_field--disabled': props.disableEditQuantity }"
                    :value="listItem.quantity"
                    @input="onQuantityInput"
                    :min="1"
                    :max="maxQuantity"
                    @wheel.prevent="blurOnWheel"
                    @blur="onQuantityBlur"
                    :readonly="props.disableEditQuantity"
                    :aria-label="`Quantity for ${listItem.item.item_name}`"
                />
                <div class="flex-row" style="gap: 0.5rem;">
                    <PencilIcon v-if="!props.disableChangeItem && shouldShowBuystackButton" id="edit-button" style="cursor: pointer; margin-left: 2px;" :alt="`Edit ${listItem.item.item_name}`" @click="openBuyStackFlyout" />
                    <TrashIcon v-if="!props.disableDeleteItem" id="delete-button" style="cursor: pointer;" :alt="`Remove ${listItem.item.item_name}`" @click="openItemDelete" />
                </div>
            </div>
            <div class="product_row-total_price title-s desktop-hidden-flex">
                <div class="paragraph-details products_row-price-free" v-if="props.freeItem">FREE</div>
                <div class="product_row-price-discount" v-if="listItem.origPrice && listItem.price != listItem.origPrice">${{ formatPrice(listItem.origPrice * listItem.quantity) }}</div>
                <div class="product_row-price-total" v-if="listItem.origPrice && listItem.price != listItem.origPrice">${{ formatPrice(listItem.price * listItem.quantity) }}</div>
                <div class="paragraph-details products_row-price" v-if="!listItem.origPrice || listItem.price == listItem.origPrice">${{ formatPrice(listItem.price * listItem.quantity) }}</div>
            </div>
        </div>
        <div class="product_row-total_price title-s mobile-hidden-flex">
            <div class="paragraph-details products_row-price-free" v-if="props.freeItem">FREE</div>
            <div class="product_row-price-discount" v-if="listItem.origPrice && listItem.price != listItem.origPrice">${{ formatPrice(listItem.origPrice * listItem.quantity) }}</div>
            <div class="product_row-price-total" v-if="listItem.origPrice && listItem.price != listItem.origPrice">${{ formatPrice(listItem.price * listItem.quantity) }}</div>
            <div class="paragraph-details products_row-price" v-if="!listItem.origPrice || listItem.price == listItem.origPrice">${{ formatPrice(listItem.price * listItem.quantity) }}</div>
        </div>
    </div>
    <p class="input-support-text invalid-state paragraph-details" v-if="props.isRestricted">This product is under a restriction. Please remove from cart to continue.</p>
    <div v-if="hasProp65(listItem)" class="flex-row-forced align-center" style="gap:0.5rem;">
        <WarningIconProp65 :id="`warning_${listItem.list_item_id}`" width="20" height="20" alt="Warning"/>
        <a @click.prevent="openProp65Modal()" class="input-support-text paragraph-details">
            <strong>WARNING:</strong> California Residents Click Here For California Prop 65 Warning.
        </a>
    </div>
    <p v-if="!props.disableEditQuantity && +listItem.quantity > maxQuantity" class="input-support-text invalid-state paragraph-details">
        The quantity entered exceeds the maximum allowed of {{ maxQuantity }}
    </p>    
</div>
</template>
<script lang="ts" setup>
    import { computed, inject } from "vue";
    import { useLists } from "@/helpers/composables/useList";
    import { router } from "@inertiajs/vue3";
    import { formatPrice } from "@/helpers/composables/usePriceFormatting"
    import { useCheckoutD2CStore } from '@/store/checkoutD2CStore'; 
    import { useDialog } from "primevue/usedialog";
    import { storeToRefs } from "pinia";
    import Prop65WarningModal from "@/components/Prop65WarningModal.vue";
    import PencilIcon from "@icons/pencil.svg";
    import TrashIcon from "@icons/trash-icon.svg";
    import WarningIconProp65 from "@icons/warning-icon.svg";
    import { comingSoonImages } from "@/helpers/composables/comingSoonImages"
    import {
        productUploadImageResponsiveFromFilename,
        PRODUCT_IMAGE_SIZES_CART_THUMB
    } from "@/helpers/productUploadImageUrl";
    import { useSettingsStore } from "@/store/settings";
    import { useStorage } from '@vueuse/core';
    import {
        getPseudoQtyForItem,
        quantityForBackendWithPseudoLine,
    } from "@/helpers/composables/useCouponPseudoLine";
    let route = inject('route');

    let dialog = useDialog();

    const settingsStore = useSettingsStore();
    const { toggleBuystackFlyout } = settingsStore;

    let list_item = defineModel("listItem", {
        type: Object,
        default: ()=>{
            return {};
        }
    });

    const couponCode = useStorage("coupon", null);

    let props = defineProps({
        disableEditQuantity: {
            type: Boolean,
            default: false
        },
        disableChangeItem: {
            type: Boolean,
            default: false
        },
        disableDeleteItem: {
            type: Boolean,
            default: false
        },
        freeItem: {
            type: Boolean,
            default: false
        },
        isRestricted: {
            type: Boolean,
            default: false
        },
        isProp65: {
            type: Boolean,
            default: false
        },
        showQuantityInDetails: {
            type: Boolean,
            default: false
        }
    });

    const {
        recalculateTax, reloadOrderInfo, getShippingRates, reapplyCoupon
    } = useCheckoutD2CStore();

    const {
        isUpdatingCartItem,
        couponInformation,
    } = storeToRefs(useCheckoutD2CStore());

    let { updateListItem, updateCouponItem } = useLists({ router, route });

    const emit = defineEmits(["openItemDeleteModal"]);

    const pseudoQty = computed(() =>
        getPseudoQtyForItem(
            couponInformation.value?.items,
            list_item.value?.item_variation_id
        )
    );

    const maxQuantity = computed(() => {
        const inventory = +list_item.value?.variation?.inventory_level || 0;

        if (pseudoQty.value <= 0) {
            return inventory;
        }

        return Math.max(1, inventory - pseudoQty.value);
    });

    let listItem = computed({
        get(){
            return {
                ...list_item.value,
                origPrice: list_item?.value?.unit_price,
                item_no: list_item?.value?.variation?.rothcoItem?.rothco_item_no,
                original_quantity: list_item?.value?.quantity,
                item: (() => {
                    const fn = list_item?.value?.variation?.primary_image?.image_filename;
                    if (fn) {
                        const pack = productUploadImageResponsiveFromFilename(
                            fn,
                            PRODUCT_IMAGE_SIZES_CART_THUMB
                        );
                        return {
                            item_name: list_item?.value?.variation.item.name,
                            variants: list_item?.value?.variation.text_description,
                            inventory_level: list_item?.value?.variation.inventory_level,
                            item_picture: pack.src,
                            item_picture_srcset: pack.srcSet,
                            item_picture_sizes: pack.sizes
                        };
                    }
                    return {
                        item_name: list_item?.value?.variation.item.name,
                        variants: list_item?.value?.variation.text_description,
                        inventory_level: list_item?.value?.variation.inventory_level,
                        item_picture: comingSoonImages().small
                    };
                })()
            }
        },
        set(value){
            list_item.value.quantity = value.quantity;
        }
    })

    const shouldShowBuystackButton = computed(() => {
        if (!!props.freeItem) {
            const variationIds =
                couponInformation.value?.freeItemVariations?.ids ?? [];
            return variationIds.length > 1;
        } else {
            return true;
        }
    });

    const freeItemSpecificationGroupNames = computed(() => {
        return couponInformation.value?.freeItemVariations?.specificationGroupNames ?? ['Style'];
    });

    let openBuyStackFlyout = function () {
        if (props.disableChangeItem) {
            return;
        }
        if (!shouldShowBuystackButton.value) {
            openProductPage(listItem.value.variation.item.seo_name);
            return;
        }
        toggleBuystackFlyout(
            {
                seo_name: listItem.value.variation.item.seo_name,
                rothco_item_no: listItem.value.variation.rothcoItem.rothco_item_no,
                item_index: listItem.value.variation.item.item_index,
                disable_quantity_input: props.disableEditQuantity,
                open_details_to_new_tab: true,
                item_list_info: {
                    list_id: listItem.value.list_id, 
                    list_item_id: listItem.value.list_item_id, 
                    quantity: listItem.value.quantity,
                },
                coupon_info: props.freeItem
                    ? {
                          freeItemVariationIds:
                              couponInformation.value?.freeItemVariations?.ids ?? [],
                      }
                    : null,
                onListItemUpdate: (updates) => {
                    if(updates?.quantity != null){
                        list_item.value.quantity = updates.quantity;
                    }
                    if (updates?.item_variation_id != null) {
                        list_item.value.item_variation_id = updates.item_variation_id;
                    }
                    if(list_item.value?.list_item_id){
                        updateItem(list_item.value);
                    }else{
                        updateCouponItemSelection({
                            list_id: list_item.value.list_id,
                            item_variation_id: updates?.item_variation_id
                        });
                    }
                }
            }
        );
    };

    let openItemDelete = function () {
        if (props.disableDeleteItem) {
            return;
        }
        emit("openItemDeleteModal", listItem);
    };

    const onQuantityInput = (event: Event) => {
        if (props.disableEditQuantity) {
            return;
        }

        isUpdatingCartItem.value = true;
        listItem.value.original_quantity = listItem.value.original_quantity ?? listItem.value.quantity;
        listItem.value.quantity = event.target.value;
    };

    const onQuantityBlur = (event: Event) => {
        if (props.disableEditQuantity) {
            return;
        }

        handleQuantityInput(listItem.value, event);
    };

    let handleQuantityInput = (row, event: Event)=>{
        if(+listItem.value.quantity <= 0){
            listItem.value.quantity = 1;
        }else if(+listItem.value.quantity > maxQuantity.value){
            listItem.value.quantity = maxQuantity.value;
        }
        if(listItem.value.quantity != listItem.value.original_quantity){
            listItem.value.original_quantity = listItem.value.quantity;
            updateItem(listItem.value);
        }else{
            isUpdatingCartItem.value = false;
        }
    };

    const updateItem = function (listItem) {
        const payload = {
            ...listItem,
            quantity: quantityForBackendWithPseudoLine(
                listItem.quantity,
                pseudoQty.value
            ),
        };

        updateListItem(payload, {
            afterFetch: () => {
                isUpdatingCartItem.value = false;
                recalculateTax();
                getShippingRates();
                reapplyCoupon();
                reloadOrderInfo();
            },
            onFetchError: ()=>{
                isUpdatingCartItem.value = false;
            }
        });
    };

    const updateCouponItemSelection = function (listItem) {
        updateCouponItem({
            coupon: couponCode.value,
            list_id: listItem.list_id,
            item_variation_id: listItem.item_variation_id,
        }, {
            afterFetch: (ctx) => {
                isUpdatingCartItem.value = false;
                recalculateTax();
                getShippingRates();
                reapplyCoupon();
                reloadOrderInfo();
            },
            onFetchError: ()=>{
                isUpdatingCartItem.value = false;
            }
        });
    };

    function blurOnWheel(event: WheelEvent) {
        event.target.blur();
    }

    function openProp65Modal(){
        dialog.open(Prop65WarningModal, {
            props: {
                showHeader: false,
                modal: true
            },
            data: {
                prop65Spec: listItem.value?.prop_65_specs.map(spec => spec.value.spec_value)
            }
        });
    }

    function hasProp65(listItem) {
        return listItem?.prop_65_specs && listItem?.prop_65_specs.length > 0;
    }

    const openProductPage = (seo_name)=>{
        window.open(route('productDetail.show', [seo_name]), "_blank");
    }
</script>